import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuthStore } from '@/stores/auth-store'
import type { Json } from '@/types/supabase'

// New block-based model — replaces old SectionConfig (type + template)
export interface BlockConfig {
  id: string         // unique instance ID (crypto.randomUUID())
  component: string  // "hero-banner", "members-grid", "cta-centered", etc.
  enabled: boolean
  order: number
  settings: Record<string, unknown>
}

// Backward compat: old SectionConfig shape (type + template)
interface LegacySectionConfig {
  type: string
  template: string
  enabled: boolean
  order: number
  settings: Record<string, unknown>
}

function normalizeToBlocks(data: (BlockConfig | LegacySectionConfig)[]): BlockConfig[] {
  return data.map((s) => {
    if ('component' in s) return s as BlockConfig
    // Convert old format: type + template → component string
    return {
      id: (s as LegacySectionConfig).type,
      component: `${(s as LegacySectionConfig).type}-${(s as LegacySectionConfig).template}`,
      enabled: s.enabled,
      order: s.order,
      settings: s.settings,
    }
  })
}

export const DEFAULT_HOMEPAGE_BLOCKS: BlockConfig[] = [
  { id: 'hero',    component: 'hero-banner',  enabled: true, order: 1, settings: {} },
  { id: 'members', component: 'members-grid', enabled: true, order: 2, settings: { max_count: 8 } },
  { id: 'events',  component: 'events-cards', enabled: true, order: 3, settings: { max_count: 3 } },
  { id: 'cheki',   component: 'cheki-banner', enabled: true, order: 4, settings: {} },
]

export function useHomepageSections() {
  const tenantId = useAuthStore((s) => s.currentTenant?.id)

  return useQuery({
    queryKey: ['homepage-sections', tenantId],
    enabled: !!tenantId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('tenants')
        .select('homepage_sections')
        .eq('id', tenantId!)
        .single()

      if (error) throw error
      const raw = data.homepage_sections as unknown as (BlockConfig | LegacySectionConfig)[] | null
      if (!raw) return DEFAULT_HOMEPAGE_BLOCKS
      return normalizeToBlocks(raw)
    },
  })
}

export function useUpdateHomepageSections() {
  const qc = useQueryClient()
  const tenantId = useAuthStore((s) => s.currentTenant?.id)

  return useMutation({
    mutationFn: async (blocks: BlockConfig[]) => {
      const { error } = await supabase
        .from('tenants')
        .update({ homepage_sections: blocks as unknown as Json })
        .eq('id', tenantId!)

      if (error) throw error
      return blocks
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['homepage-sections', tenantId] })
      qc.invalidateQueries({ queryKey: ['tenant'] })
    },
  })
}
